import React, { useState } from "react";
import { AlertCircle, X, ChevronDown, ChevronUp } from "lucide-react";

interface ErrorBannerProps {
  message: string;
  fullMessage?: string; // полный текст ошибки; при наличии показывается кнопка «Показать полностью»
  onClose?: () => void;
  isCheckError?: boolean; // true если это ошибка проверки, false если ошибка выполнения
}

export const ErrorBanner: React.FC<ErrorBannerProps> = ({ message, fullMessage, onClose, isCheckError = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const canExpand = fullMessage && fullMessage.length > (message?.length || 0);

  const title = isCheckError
    ? "Ошибка при проверке запроса"
    : "Ошибка при выполнении запроса";

  const displayText = canExpand && isExpanded ? fullMessage : message;

  return (
    <div
      className="mx-4 mb-4 animate-slideIn"
      role="alert"
      aria-live="assertive"
    >
      <div className="bg-red-50 border-l-4 border-red-500 rounded-lg shadow-lg p-4 flex items-start gap-3 ring-1 ring-red-200">
        <div className="flex-shrink-0 mt-0.5">
          <AlertCircle className="w-5 h-5 text-red-600" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-red-900 text-base mb-1.5">
            {title}
          </div>
          <div
            className={`text-sm text-red-800 leading-relaxed whitespace-pre-wrap break-words overflow-y-auto ${
              canExpand && isExpanded ? "max-h-[min(24rem,50vh)]" : "max-h-[12rem]"
            }`}
          >
            {displayText}
          </div>
          {canExpand && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 text-sm font-medium text-red-700 hover:text-red-800 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-red-50 rounded"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? "Свернуть полный текст ошибки" : "Показать полный текст ошибки"}
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="inline w-4 h-4 mr-1 align-middle" />
                  Свернуть
                </>
              ) : (
                <>
                  <ChevronDown className="inline w-4 h-4 mr-1 align-middle" />
                  Показать полностью
                </>
              )}
            </button>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1.5 rounded-md hover:bg-red-100 active:bg-red-200 transition-colors"
            aria-label="Закрыть ошибку"
            title="Закрыть"
          >
            <X size={18} className="text-red-600" />
          </button>
        )}
      </div>
    </div>
  );
};
